/**
 * Client-side Quran search over slim language-split indexes + MiniSearch.
 *
 * Files under /surah-data/:
 *   search-core.json  — arabic + surah meta (always)
 *   search-gu|en|ur.json — translation + tafsir per language
 */

import MiniSearch from 'minisearch';
import { QURAN_DATA_VERSION } from '../config/quranDataVersion';

const DATA_PREFIX = '/surah-data';
/** Initial page size on /search; "Show more" loads additional pages. */
export const RESULTS_PAGE_SIZE = 40;
/** Hard cap so one query cannot freeze the UI. */
export const MAX_RESULTS = 500;
const QUICK_RESULTS = 8;

const LANG_FILE = {
  gujarati: 'search-gu.json',
  english: 'search-en.json',
  urdu: 'search-ur.json',
  gu: 'search-gu.json',
  en: 'search-en.json',
  ur: 'search-ur.json',
};

/** Map TranslationContext / i18n codes → index lang keys (user preference only). */
export function resolvePreferredLangs(selectedTranslations = [], i18nLang) {
  const out = [];
  const seen = new Set();
  const add = (code) => {
    if (!code) return;
    const c = String(code).toLowerCase();
    let lang = null;
    if (c.startsWith('gu') || c === 'gujarati') lang = 'gu';
    else if (c.startsWith('en') || c === 'english') lang = 'en';
    else if (c.startsWith('ur') || c === 'urdu') lang = 'ur';
    if (lang && !seen.has(lang)) {
      seen.add(lang);
      out.push(lang);
    }
  };
  (selectedTranslations || []).forEach(add);
  add(i18nLang);
  if (!out.length) out.push('gu');
  return out;
}

/** Languages to load/search: preferred + English for recall. */
export function resolveSearchLangs(selectedTranslations = [], i18nLang) {
  const preferred = resolvePreferredLangs(selectedTranslations, i18nLang);
  const out = new Set(preferred);
  out.add('en');
  return Array.from(out);
}

export const LANG_LABELS = {
  gu: 'Gujarati',
  en: 'English',
  ur: 'Urdu',
};

export function normalizeForSearch(text) {
  if (text == null || text === '') return '';
  return (
    String(text)
      .normalize('NFKD')
      // Latin diacritics
      .replace(/[\u0300-\u036f]/g, '')
      // Arabic tashkeel (when mixed into translation text)
      .replace(/[\u064B-\u065F\u0670]/g, '')
      // Gujarati: candrabindu, anusvara, visarga (પસંદ ↔ પસદ)
      .replace(/[\u0A81-\u0A83]/g, '')
      // Devanagari: candrabindu, anusvara, visarga
      .replace(/[\u0901-\u0903]/g, '')
      // Nukta marks (Gujarati / Devanagari / etc.)
      .replace(/[\u093C\u09BC\u0A3C\u0ABC\u0B3C]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * MiniSearch fuzzy is a fraction of term length (not absolute edits).
 * Short words need a higher fraction or they get 0 allowed edits.
 */
function fuzzyForTerm(term) {
  const len = String(term || '').length;
  if (len <= 2) return false;
  // ≥1 edit for short terms; ~2 for medium; up to ~25% for long
  const forOneEdit = 1 / len + 0.02;
  return Math.min(0.4, Math.max(0.25, forOneEdit));
}

export function normalizeArabic(text) {
  if (!text) return '';
  return String(text)
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, '')
    .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627')
    .replace(/\u0629/g, '\u0647')
    .replace(/\u0649/g, '\u064A')
    .replace(/[^\u0621-\u064A\u0660-\u0669\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function versionedUrl(path) {
  return `${path}?v=${encodeURIComponent(QURAN_DATA_VERSION)}`;
}

function tokenizeQuery(query) {
  const raw = String(query || '').trim();
  if (!raw) return [];
  const arabicNorm = normalizeArabic(raw);
  const hasArabic = /[\u0600-\u06FF]/.test(raw);
  if (hasArabic && arabicNorm) {
    return arabicNorm.split(/\s+/).filter(Boolean);
  }
  return normalizeForSearch(raw)
    .split(/[^a-z0-9\u0a80-\u0aff]+/i)
    .filter((t) => t.length > 0);
}

function stripHtml(text) {
  if (!text) return '';
  return String(text).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function snippetAround(text, query, radius = 70) {
  const clean = stripHtml(text);
  if (!clean) return '';
  const nText = normalizeForSearch(clean);
  const tokens = tokenizeQuery(query);
  let idx = -1;
  let matchLen = 0;
  for (const t of tokens) {
    const i = nText.indexOf(normalizeForSearch(t));
    if (i !== -1) {
      idx = i;
      matchLen = t.length;
      break;
    }
  }
  if (idx === -1) {
    return clean.length > radius * 2 ? `${clean.slice(0, radius * 2)}…` : clean;
  }
  // Map approx from normalized to original by scanning (good enough for snippets)
  const start = Math.max(0, idx - radius);
  const end = Math.min(clean.length, idx + matchLen + radius);
  const slice = clean.slice(start, end);
  return `${start > 0 ? '…' : ''}${slice}${end < clean.length ? '…' : ''}`;
}

/** @type {Map<string, object>} */
const coreById = new Map();
/** @type {Map<string, Map<string, { tr: string, tf: string }>>} */
const langById = new Map(); // lang -> id -> {tr, tf}

let miniSearch = null;
let loadedLangs = new Set();
let coreLoaded = false;
let loadPromise = null;

function rebuildMiniSearch() {
  const docs = [];
  for (const [id, core] of coreById) {
    const parts = {
      id,
      surah_id: core.s,
      ayah_number: core.n,
      surah_name: core.sn || '',
      surah_name_gujarati: core.sg || '',
      arabic: core.a || '',
      arabic_norm: core.an || normalizeArabic(core.a),
      translation: '',
      tafsir: '',
    };
    const trParts = [];
    const tfParts = [];
    for (const lang of loadedLangs) {
      const pack = langById.get(lang)?.get(id);
      if (pack?.tr) trParts.push(pack.tr);
      if (pack?.tf) tfParts.push(pack.tf);
    }
    parts.translation = trParts.join(' ');
    parts.tafsir = tfParts.join(' ');
    docs.push(parts);
  }

  miniSearch = new MiniSearch({
    fields: [
      'arabic_norm',
      'translation',
      'tafsir',
      'surah_name',
      'surah_name_gujarati',
    ],
    storeFields: [
      'surah_id',
      'ayah_number',
      'surah_name',
      'surah_name_gujarati',
      'arabic',
    ],
    searchOptions: {
      boost: {
        surah_name: 4,
        surah_name_gujarati: 4,
        arabic_norm: 3,
        translation: 2.5,
        tafsir: 1,
      },
      fuzzy: fuzzyForTerm,
      prefix: true,
      combineWith: 'AND',
    },
    processTerm: (term, _fieldName) => {
      if (!term) return null;
      if (/[\u0600-\u06FF]/.test(term)) {
        const n = normalizeArabic(term);
        return n || null;
      }
      const n = normalizeForSearch(term);
      // Skip ultra-short noise after stripping marks
      return n.length >= 1 ? n : null;
    },
  });
  miniSearch.addAll(docs);
}

async function fetchJson(url, signal) {
  const res = await fetch(versionedUrl(url), { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  return res.json();
}

async function ensureCore(signal) {
  if (coreLoaded) return;
  const data = await fetchJson(`${DATA_PREFIX}/search-core.json`, signal);
  const docs = data.docs || data;
  for (const d of docs) {
    coreById.set(d.i, d);
  }
  coreLoaded = true;
}

async function ensureLang(lang, signal) {
  if (loadedLangs.has(lang)) return;
  const file = LANG_FILE[lang] || `search-${lang}.json`;
  const data = await fetchJson(`${DATA_PREFIX}/${file}`, signal);
  const docs = data.docs || data;
  const map = new Map();
  for (const d of docs) {
    map.set(d.i, { tr: d.tr || '', tf: d.tf || '' });
  }
  langById.set(lang, map);
  loadedLangs.add(lang);
}

/**
 * Load core + language packs, rebuild MiniSearch when packs change.
 * @param {string[]} langs e.g. ['gu','en']
 * @param {AbortSignal} [signal]
 */
export async function ensureSearchReady(langs = ['gu'], signal) {
  const needed = Array.from(new Set(langs.length ? langs : ['gu']));

  const run = async () => {
    await ensureCore(signal);
    let changed = false;
    for (const lang of needed) {
      if (!loadedLangs.has(lang)) {
        await ensureLang(lang, signal);
        changed = true;
      }
    }
    if (!miniSearch || changed) rebuildMiniSearch();
  };

  // Serialize concurrent callers so language packs don't race.
  while (loadPromise) {
    await loadPromise;
  }
  if (coreLoaded && needed.every((l) => loadedLangs.has(l)) && miniSearch) {
    return;
  }

  loadPromise = run().finally(() => {
    loadPromise = null;
  });
  await loadPromise;
}

export function clearSearchIndexCache() {
  coreById.clear();
  langById.clear();
  loadedLangs = new Set();
  coreLoaded = false;
  miniSearch = null;
  loadPromise = null;
}

/**
 * Prefetch on idle — call once after app mount.
 * @param {string[]} [langs]
 */
export function prefetchSearchIndex(langs = ['gu']) {
  const run = () => {
    ensureSearchReady(langs).catch(() => {});
  };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1500);
  }
}

function detectMatchFields(doc, query, langs) {
  const tokens = tokenizeQuery(query);
  const fields = { arabic: false, translation: false, tafsir: false, surah: false };
  if (!tokens.length) return fields;

  const arabicHay = `${doc.arabic || ''} ${doc.arabic_norm || ''}`;
  const arabicN = normalizeArabic(arabicHay);
  const surahHay = normalizeForSearch(`${doc.surah_name || ''} ${doc.surah_name_gujarati || ''}`);

  let tr = '';
  let tf = '';
  for (const lang of langs) {
    const pack = langById.get(lang)?.get(doc.id);
    if (pack?.tr) tr += ` ${pack.tr}`;
    if (pack?.tf) tf += ` ${pack.tf}`;
  }
  const trN = normalizeForSearch(tr);
  const tfN = normalizeForSearch(tf);

  for (const t of tokens) {
    const isAr = /[\u0600-\u06FF]/.test(t) || normalizeArabic(t).length === t.replace(/\s/g, '').length;
    const needle = isAr ? normalizeArabic(t) : normalizeForSearch(t);
    if (!needle) continue;
    if (arabicN.includes(needle)) fields.arabic = true;
    if (trN.includes(needle)) fields.translation = true;
    if (tfN.includes(needle)) fields.tafsir = true;
    if (surahHay.includes(normalizeForSearch(t))) fields.surah = true;
  }
  return fields;
}

function textContainsAnyToken(text, query) {
  const tokens = tokenizeQuery(query);
  if (!tokens.length || !text) return false;
  const n = normalizeForSearch(text);
  const a = normalizeArabic(text);
  const queryHasArabic = /[\u0600-\u06FF]/.test(String(query || ''));
  return tokens.some((t) => {
    if (queryHasArabic || /[\u0600-\u06FF]/.test(t)) {
      const needle = normalizeArabic(t);
      return needle && a.includes(needle);
    }
    const needle = normalizeForSearch(t);
    return needle && n.includes(needle);
  });
}

function pickDisplayTexts(id, searchLangs, preferredLangs, query) {
  const preferred = preferredLangs[0] || 'gu';

  const matchedTrLangs = [];
  const matchedTfLangs = [];
  for (const lang of searchLangs) {
    const pack = langById.get(lang)?.get(id);
    if (!pack) continue;
    if (pack.tr && textContainsAnyToken(pack.tr, query)) matchedTrLangs.push(lang);
    if (pack.tf && textContainsAnyToken(pack.tf, query)) matchedTfLangs.push(lang);
  }

  const pickPack = (lang) => langById.get(lang)?.get(id);

  // Primary = language that matched the query; fall back to preferred / any loaded.
  const trOrder = [
    ...matchedTrLangs,
    preferred,
    ...preferredLangs,
    ...searchLangs,
    'en',
    'gu',
    'ur',
  ];
  const tfOrder = [
    ...matchedTfLangs,
    preferred,
    ...preferredLangs,
    ...searchLangs,
    'en',
    'gu',
    'ur',
  ];

  let translation = '';
  let translationLang = '';
  for (const lang of trOrder) {
    const pack = pickPack(lang);
    if (pack?.tr) {
      translation = pack.tr;
      translationLang = lang;
      break;
    }
  }

  let tafsir = '';
  let tafsirLang = '';
  for (const lang of tfOrder) {
    const pack = pickPack(lang);
    if (pack?.tf) {
      tafsir = pack.tf;
      tafsirLang = lang;
      break;
    }
  }

  // Secondary: user's Settings language when different from the matched/display lang
  let preferredTranslation = '';
  let preferredTranslationLang = '';
  if (preferred && preferred !== translationLang) {
    const pack = pickPack(preferred);
    if (pack?.tr) {
      preferredTranslation = pack.tr;
      preferredTranslationLang = preferred;
    }
  }

  let preferredTranslationSnippet = '';
  if (preferredTranslation) {
    preferredTranslationSnippet = textContainsAnyToken(preferredTranslation, query)
      ? snippetAround(preferredTranslation, query)
      : preferredTranslation.length > 140
        ? `${preferredTranslation.slice(0, 140)}…`
        : preferredTranslation;
  }

  return {
    translation,
    tafsir,
    translationLang,
    tafsirLang,
    translationSnippet: snippetAround(translation, query),
    tafsirSnippet: snippetAround(tafsir, query),
    preferredTranslation,
    preferredTranslationLang,
    preferredTranslationSnippet,
    matchedTranslationLang: matchedTrLangs[0] || '',
    matchedTafsirLang: matchedTfLangs[0] || '',
  };
}

/**
 * @typedef {'all'|'arabic'|'translation'|'tafsir'} SearchScope
 */

/**
 * @param {string} query
 * @param {object} [options]
 * @param {string[]} [options.langs]
 * @param {string[]} [options.preferredLangs]
 * @param {SearchScope} [options.scope]
 * @param {number} [options.limit]
 * @param {number} [options.offset]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ results: object[], total: number, hasMore: boolean }>}
 */
export async function searchAyahsLocal(query, options = {}) {
  const q = String(query || '').trim();
  if (!q) return { results: [], total: 0, hasMore: false };

  const langs = options.langs?.length ? options.langs : ['gu', 'en'];
  const preferredList = options.preferredLangs?.length
    ? options.preferredLangs
    : [langs.find((l) => l !== 'en') || langs[0] || 'gu'];
  const scope = options.scope || 'all';
  const limit = options.limit ?? MAX_RESULTS;
  const offset = Math.max(0, options.offset || 0);
  const signal = options.signal;

  await ensureSearchReady(langs, signal);
  if (!miniSearch) return { results: [], total: 0, hasMore: false };

  const hasArabic = /[\u0600-\u06FF]/.test(q);
  const searchQuery = hasArabic ? normalizeArabic(q) || q : normalizeForSearch(q) || q;

  let raw;
  try {
    raw = miniSearch.search(searchQuery, {
      filter: undefined,
    });
  } catch {
    return { results: [], total: 0, hasMore: false };
  }

  const enriched = [];
  for (const hit of raw) {
    const fields = detectMatchFields(hit, q, langs);
    if (scope === 'arabic' && !fields.arabic && !fields.surah) continue;
    if (scope === 'translation' && !fields.translation && !fields.surah) continue;
    if (scope === 'tafsir' && !fields.tafsir) continue;

    const display = pickDisplayTexts(hit.id, langs, preferredList, q);
    const langForBadge = display.matchedTranslationLang || display.translationLang;
    const matchLabel = fields.surah
      ? 'Surah'
      : fields.arabic
        ? 'Arabic'
        : fields.translation
          ? `Translation · ${LANG_LABELS[langForBadge] || langForBadge || 'Match'}`
          : fields.tafsir
            ? `Tafsir · ${LANG_LABELS[display.matchedTafsirLang || display.tafsirLang] || 'Match'}`
            : 'Match';

    enriched.push({
      ayah_id: hit.id,
      surah_id: hit.surah_id,
      ayah_number: hit.ayah_number,
      surah_name: hit.surah_name,
      surah_name_gujarati: hit.surah_name_gujarati,
      arabic_text: hit.arabic,
      translation_gujarati: display.translationLang === 'gu' ? display.translation : '',
      translation_english: display.translationLang === 'en' ? display.translation : '',
      translation_urdu: display.translationLang === 'ur' ? display.translation : '',
      tafsir_gujarati: display.tafsirLang === 'gu' ? display.tafsir : '',
      tafsir_english: display.tafsirLang === 'en' ? display.tafsir : '',
      tafsir_urdu: display.tafsirLang === 'ur' ? display.tafsir : '',
      score: hit.score,
      matchFields: fields,
      matchLabel,
      translationText: display.translation,
      tafsirText: display.tafsir,
      translationSnippet: display.translationSnippet,
      tafsirSnippet: display.tafsirSnippet,
      translationLang: display.translationLang,
      tafsirLang: display.tafsirLang,
      preferredTranslationSnippet: display.preferredTranslationSnippet,
      preferredTranslationLang: display.preferredTranslationLang,
    });

    if (enriched.length >= MAX_RESULTS) break;
  }

  const total = enriched.length;
  const results = enriched.slice(offset, offset + limit);
  return {
    results,
    total,
    hasMore: offset + results.length < total,
  };
}

/** Quick preview for header dropdown — returns an array. */
export async function searchAyahsQuick(query, options = {}) {
  const { results } = await searchAyahsLocal(query, {
    ...options,
    limit: options.limit || QUICK_RESULTS,
    offset: 0,
  });
  return results;
}

export { QUICK_RESULTS };
